/**
 * NotificationToast - Sidebar-anchored real-time notification toasts.
 *
 * Toasts slide out from the left edge (sidebar), creating a visual connection
 * to the notification bell icon. Clicking marks the notification as read,
 * navigates to its link, and dismisses. Hovering pauses auto-dismiss.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import { PiBellRingingLight, PiXLight, PiArrowRightLight } from "react-icons/pi";
import { useTranslation } from "react-i18next";

// ── types ────────────────────────────────────────────────────────────────────
export interface ToastNotification {
  id: string;
  title?: string;
  content: string;
  link?: string;
  type?: string;
}

interface ToastItemProps {
  toast: ToastNotification;
  onDismiss: (id: string) => void;
  onNavigate: (link: string) => void;
  onMarkAsRead: (id: string) => void;
  index: number;
}

// ── constants ────────────────────────────────────────────────────────────────
const AUTO_DISMISS_MS = 6000;
const MAX_VISIBLE = 3;

// ── single toast ─────────────────────────────────────────────────────────────
const ToastItem: React.FC<ToastItemProps> = ({
  toast,
  onDismiss,
  onNavigate,
  onMarkAsRead,
  index,
}) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<"enter" | "visible" | "exit">("enter");
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [isHovered, setIsHovered] = useState(false);
  const [progressPaused, setProgressPaused] = useState(false);

  // slide-in on mount (staggered per index)
  useEffect(() => {
    const delay = index * 100;
    const timer = setTimeout(() => {
      requestAnimationFrame(() => setPhase("visible"));
    }, delay);
    return () => clearTimeout(timer);
  }, [index]);

  // auto-dismiss timer - pause on hover
  useEffect(() => {
    if (phase !== "visible") return;
    if (isHovered) {
      clearTimeout(timerRef.current);
      setProgressPaused(true);
      return;
    }
    setProgressPaused(false);
    timerRef.current = setTimeout(() => setPhase("exit"), AUTO_DISMISS_MS);
    return () => clearTimeout(timerRef.current);
  }, [phase, isHovered]);

  const handleTransitionEnd = () => {
    if (phase === "exit") onDismiss(toast.id);
  };

  const handleClick = () => {
    onMarkAsRead(toast.id);
    if (toast.link) onNavigate(toast.link);
    setPhase("exit");
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearTimeout(timerRef.current);
    setPhase("exit");
  };

  const typeLabel = toast.type
    ? toast.type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : undefined;

  return (
    <div
      onClick={handleClick}
      onTransitionEnd={handleTransitionEnd}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="alert"
      className="group relative flex items-center gap-2.5 w-[300px] ps-3 pe-2.5 py-2 glass !border-l-0 rounded-e-xl shadow-xl cursor-pointer select-none"
      style={{
        willChange: "transform, opacity",
        transformOrigin: "left center",
        transition: `all ${phase === "exit" ? "200ms" : "400ms"} cubic-bezier(0.34, 1.56, 0.64, 1)`,
        opacity: phase === "visible" ? 1 : 0,
        transform:
          phase === "enter"
            ? "translateY(16px) scale(0.96)"
            : phase === "visible"
            ? "translateY(0) scale(1)"
            : "translateY(-12px) scale(0.96)",
      }}
    >
      {/* Left accent bar - visual link to sidebar */}
      <div className="absolute start-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-info-400 via-blue-500 to-info-600 dark:from-info-400 dark:via-blue-500 dark:to-info-600" />

      {/* Bell icon with ring animation on entry */}
      <div className="flex-shrink-0">
        <div className="w-6 h-6 rounded-md bg-info-50 dark:bg-info-500/10 flex items-center justify-center relative overflow-hidden">
          <PiBellRingingLight
            className="w-3.5 h-3.5 text-info-500 dark:text-info-400"
            style={{
              animation: phase === "visible" ? "notif-bell-ring 0.6s ease-in-out" : "none",
            }}
          />
        </div>
      </div>

      {/* Text content */}
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="text-[11px] font-medium text-gray-900 dark:text-gray-100 leading-tight truncate">
            {toast.title}
          </p>
        )}
        <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-snug line-clamp-1 mt-px">
          {toast.content}
        </p>
        <div className="flex items-center gap-2 mt-1">
          {typeLabel && (
            <span className="text-[9px] font-medium uppercase tracking-wider text-info-500/70 dark:text-info-400/70">
              {typeLabel}
            </span>
          )}
          {toast.link && (
            <span className="flex items-center gap-0.5 text-[9px] font-medium text-info-500 dark:text-info-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              View <PiArrowRightLight className="w-2.5 h-2.5 rtl:-scale-x-100" />
            </span>
          )}
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={handleClose}
        className="
          flex-shrink-0 mt-0.5 p-1 rounded-md
          text-gray-400 dark:text-gray-500
          opacity-0 group-hover:opacity-100
          hover:bg-gray-100 dark:hover:bg-gray-700
          transition-all duration-200
        "
        aria-label={t('Dismiss')}
      >
        <PiXLight className="w-3.5 h-3.5" />
      </button>

      {/* Progress bar - pauses on hover */}
      <div className="absolute bottom-0 start-[3px] end-3 h-[2px] rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700/50">
        <div
          className="h-full bg-gradient-to-r from-info-400 to-info-500 dark:from-info-500 dark:to-info-400 rounded-full"
          style={{
            animation:
              phase === "visible"
                ? `notif-toast-progress ${AUTO_DISMISS_MS}ms linear forwards`
                : "none",
            animationPlayState: progressPaused ? "paused" : "running",
          }}
        />
      </div>
    </div>
  );
};

// ── toast container (portal) ─────────────────────────────────────────────────
interface NotificationToastContainerProps {
  toasts: ToastNotification[];
  onDismiss: (id: string) => void;
  onMarkAsRead: (id: string) => void;
  sidebarWidth?: number;
}

export const NotificationToastContainer: React.FC<NotificationToastContainerProps> = ({
  toasts,
  onDismiss,
  onMarkAsRead,
  sidebarWidth = 56,
}) => {
  const history = useHistory();

  const handleNavigate = useCallback(
    (link: string) => {
      history.push(link);
    },
    [history]
  );

  const visible = toasts.slice(-MAX_VISIBLE);

  if (!visible.length) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed z-[99999] flex flex-col-reverse gap-2 pointer-events-none"
      style={{
        left: `${sidebarWidth}px`,
        top: sidebarWidth <= 56 ? "100px" : "120px",
      }}
      aria-live="polite"
    >
      {visible.map((t, i) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem
            toast={t}
            onDismiss={onDismiss}
            onNavigate={handleNavigate}
            onMarkAsRead={onMarkAsRead}
            index={i}
          />
        </div>
      ))}
    </div>,
    document.body
  );
};

// ── CSS keyframes (injected once) ────────────────────────────────────────────
const toastCSS = `
@keyframes notif-toast-progress {
  from { width: 100%; }
  to   { width: 0%; }
}

@keyframes notif-bell-ring {
  0%   { transform: rotate(0deg); }
  15%  { transform: rotate(14deg); }
  30%  { transform: rotate(-12deg); }
  45%  { transform: rotate(10deg); }
  60%  { transform: rotate(-6deg); }
  75%  { transform: rotate(3deg); }
  100% { transform: rotate(0deg); }
}
`;

const toastStyle = document.createElement("style");
toastStyle.textContent = toastCSS;
document.head.appendChild(toastStyle);

export default NotificationToastContainer;
